// Client-side image compression.
//
// Vercel serverless functions reject request bodies larger than ~4.5MB, so a raw
// phone photo (often 3–8MB) would stall the /api/blob-upload call and make posting
// appear frozen. Downscaling + re-encoding to JPEG in the browser brings images down
// to a few hundred KB — safely under the limit and much faster on mobile networks.
//
// Videos, GIFs, SVGs and anything that isn't a raster image are returned unchanged.

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), type, quality));
}

async function loadBitmap(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file);
    } catch {
      // fall through to HTMLImageElement
    }
  }
  return await new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

export interface CompressOptions {
  maxDimension?: number; // longest edge, in px
  quality?: number;      // initial JPEG quality 0..1
  maxBytes?: number;     // target ceiling; quality steps down until met
}

export async function compressImage(file: File, opts: CompressOptions = {}): Promise<File> {
  const { maxDimension = 1600, quality = 0.82, maxBytes = 3_500_000 } = opts;

  // Only touch raster images we can safely re-encode as JPEG.
  if (!file.type.startsWith('image/') || file.type === 'image/gif' || file.type === 'image/svg+xml') {
    return file;
  }

  try {
    const bitmap = await loadBitmap(file);
    const width = (bitmap as any).width as number;
    const height = (bitmap as any).height as number;
    const scale = Math.min(1, maxDimension / Math.max(width, height));

    // Already small enough and no downscale needed — keep the original bytes.
    if (scale === 1 && file.size <= maxBytes) {
      (bitmap as any).close?.();
      return file;
    }

    const targetW = Math.max(1, Math.round(width * scale));
    const targetH = Math.max(1, Math.round(height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext('2d');
    if (!ctx) { (bitmap as any).close?.(); return file; }
    ctx.drawImage(bitmap as any, 0, 0, targetW, targetH);
    (bitmap as any).close?.();

    // Step quality down until the encoded blob fits under maxBytes (or we bottom out).
    let q = quality;
    let blob = await canvasToBlob(canvas, 'image/jpeg', q);
    while (blob && blob.size > maxBytes && q > 0.4) {
      q -= 0.15;
      blob = await canvasToBlob(canvas, 'image/jpeg', q);
    }
    if (!blob || blob.size >= file.size) return file; // never end up larger than the source

    const newName = file.name.replace(/\.(png|webp|jpe?g|bmp|tiff?)$/i, '') + '.jpg';
    return new File([blob], newName, { type: 'image/jpeg', lastModified: Date.now() });
  } catch {
    // Compression must never block a post; fall back to the original file.
    return file;
  }
}
